import { Close, InfoOutlined, Verified } from '@mui/icons-material';
import { Box, Checkbox, CircularProgress, Tab, Tabs, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useFetchData } from '../../../hooks/useFetchData';
import { CustomTooltip } from '../../../theme/CustomComponents';
import { CollectionWithCreator } from '../../../Types/dealGPT';
import { CollectionTags } from '../../../Types/enums';
import { collectionsPathUrl } from '../../../util/routesUrls';
import { useAuth } from '../../Providers/AuthProvider';

export enum QuestionPreferencesDialogTabs {
  PUBLIC_COLLECTIONS = 0,
  MY_COLLECTIONS = 1,
  SHARED_COLLECTIONS = 2,
}

interface QuestionPreferencesDialogProps {
  runScopes: string[];
  onClose: () => void;
  handleQuestionsPreferencesChanged: (newRun: boolean, collections: string[]) => void;
}

export default function QuestionPreferencesDialog({
  runScopes,
  onClose,
  handleQuestionsPreferencesChanged,
}: QuestionPreferencesDialogProps) {
  const { currentUser } = useAuth();
  const [selectedCollections, setSelectedCollections] = useState<string[]>([]);
  const [selectedTab, setSelectedTab] = useState<QuestionPreferencesDialogTabs>(
    QuestionPreferencesDialogTabs.PUBLIC_COLLECTIONS
  );

  const { data: userCollections, loading: loadingUserCollections } = useFetchData<
    CollectionWithCreator[]
  >({
    urlPath: collectionsPathUrl({ owners: [currentUser?.id ?? ''], excludeEmpty: true }),
    skip: !currentUser,
  });

  const { data: sharedCollections, loading: loadingSharedCollections } = useFetchData<
    CollectionWithCreator[]
  >({
    urlPath: collectionsPathUrl({ shared: true, excludeEmpty: true }),
    skip: !currentUser,
  });

  const { data: publicCollections, loading: loadingPublicCollections } = useFetchData<
    CollectionWithCreator[]
  >({
    urlPath: collectionsPathUrl({ visibility: ['public'], excludeEmpty: true }),
  });

  const collections = useMemo(() => {
    const combinedCollections = [
      ...(userCollections ?? []),
      ...(sharedCollections ?? []),
      ...(publicCollections ?? []),
    ];

    const uniqueCollections = new Map(
      combinedCollections.map((collection) => [collection.id, collection])
    ).values();

    return [...uniqueCollections].filter((collection) => collection.questionCount > 0);
  }, [userCollections, sharedCollections, publicCollections]);

  const toggleCollection = useCallback((collectionId: string) => {
    setSelectedCollections((prev) =>
      prev.includes(collectionId) ? prev.filter((c) => c !== collectionId) : [...prev, collectionId]
    );
  }, []);

  useEffect(() => {
    if (!collections) return;

    setSelectedCollections(
      collections
        .filter((collection) => runScopes.includes(collection.name))
        .map((collection) => collection.id)
    );
  }, [collections, runScopes]);

  return (
    <Dialog
      onClose={onClose}
      open
      fullWidth
      PaperProps={{ sx: { width: '630px', maxWidth: '630px' } }}
    >
      <DialogTitle>Import Questions</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <Close />
      </IconButton>
      <DialogContent
        dividers
        sx={{ minHeight: '500px', maxHeight: '500px', paddingX: 2, paddingY: 0 }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          sx={{ marginBottom: 2 }}
        >
          <Tab
            value={QuestionPreferencesDialogTabs.PUBLIC_COLLECTIONS}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Public Collections
                {loadingPublicCollections ? (
                  <CircularProgress size={20} />
                ) : (
                  ` (${publicCollections?.length})`
                )}
              </Box>
            }
          />
          <Tab
            value={QuestionPreferencesDialogTabs.MY_COLLECTIONS}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                My Collections
                {loadingUserCollections ? (
                  <CircularProgress size={20} />
                ) : (
                  ` (${userCollections?.length})`
                )}
              </Box>
            }
          />
          <Tab
            value={QuestionPreferencesDialogTabs.SHARED_COLLECTIONS}
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Shared with me
                {loadingSharedCollections ? (
                  <CircularProgress size={20} />
                ) : (
                  ` (${sharedCollections?.length})`
                )}
              </Box>
            }
          />
        </Tabs>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {selectedTab == QuestionPreferencesDialogTabs.PUBLIC_COLLECTIONS &&
            publicCollections?.map((collection) => (
              <Box
                key={collection.id}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}
                onClick={() => toggleCollection(collection.id)}
              >
                <Checkbox
                  checked={selectedCollections.includes(collection.id)}
                  onChange={() => toggleCollection(collection.id)}
                  onClick={(e) => e.stopPropagation()}
                />

                <Typography>{collection.name}</Typography>
                {collection.tags?.includes(CollectionTags.VERIFIED) && (
                  <Tooltip title="Verified Collection" arrow>
                    <Verified color="primary" />
                  </Tooltip>
                )}
                {!!collection.description && (
                  <CustomTooltip
                    title={
                      <span onClick={(e) => e.stopPropagation()}>{collection.description}</span>
                    }
                  >
                    <InfoOutlined sx={{ width: '20px' }} />
                  </CustomTooltip>
                )}
                <Typography variant="caption">
                  {collection.questionCount} question{collection.questionCount !== 1 ? 's' : ''} by{' '}
                  {collection.createdBy === currentUser?.id ? 'You' : collection.createdByName}
                </Typography>
              </Box>
            ))}
          {selectedTab == QuestionPreferencesDialogTabs.MY_COLLECTIONS &&
            userCollections?.map((collection) => (
              <Box
                key={collection.id}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}
                onClick={() => toggleCollection(collection.id)}
              >
                <Checkbox
                  checked={selectedCollections.includes(collection.id)}
                  onChange={() => toggleCollection(collection.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Typography>{collection.name}</Typography>
                {collection.tags?.includes(CollectionTags.VERIFIED) && (
                  <Tooltip title="Verified Collection" arrow>
                    <Verified color="primary" />
                  </Tooltip>
                )}
                {!!collection.description && (
                  <CustomTooltip
                    title={
                      <span onClick={(e) => e.stopPropagation()}>{collection.description}</span>
                    }
                  >
                    <InfoOutlined sx={{ width: '20px' }} />
                  </CustomTooltip>
                )}
                <Typography variant="caption">
                  {collection.questionCount} question{collection.questionCount !== 1 ? 's' : ''} by{' '}
                  {collection.createdBy === currentUser?.id ? 'You' : collection.createdByName}
                </Typography>
              </Box>
            ))}
          {selectedTab == QuestionPreferencesDialogTabs.SHARED_COLLECTIONS &&
            sharedCollections?.map((collection) => (
              <Box
                key={collection.id}
                sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 1 }}
                onClick={() => toggleCollection(collection.id)}
              >
                <Checkbox
                  checked={selectedCollections.includes(collection.id)}
                  onChange={() => toggleCollection(collection.id)}
                  onClick={(e) => e.stopPropagation()}
                />
                <Typography>{collection.name}</Typography>
                {collection.tags?.includes(CollectionTags.VERIFIED) && (
                  <Tooltip title="Verified Collection" arrow>
                    <Verified color="primary" />
                  </Tooltip>
                )}
                {!!collection.description && (
                  <CustomTooltip
                    title={
                      <span onClick={(e) => e.stopPropagation()}>{collection.description}</span>
                    }
                  >
                    <InfoOutlined sx={{ width: '20px' }} />
                  </CustomTooltip>
                )}
                <Typography variant="caption">
                  {collection.questionCount} question{collection.questionCount !== 1 ? 's' : ''} by{' '}
                  {collection.createdBy === currentUser?.id ? 'You' : collection.createdByName}
                </Typography>
              </Box>
            ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={() => window.open('/question-library', '_blank')}>
          View library
        </Button>
        <Button
          variant="outlined"
          disabled={selectedCollections.length === 0}
          onClick={() => handleQuestionsPreferencesChanged(false, selectedCollections)}
        >
          Add to current run
        </Button>
        <Button
          variant="contained"
          disabled={selectedCollections.length === 0}
          onClick={() => handleQuestionsPreferencesChanged(true, selectedCollections)}
        >
          Add to new run
        </Button>
      </DialogActions>
    </Dialog>
  );
}
